import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Empty,
    Input,
    Modal,
    Skeleton,
    Space,
    Tag,
    Typography,
    Tooltip,
    Select,
    message,
} from 'antd';
import {
    LockOutlined,
    SettingOutlined,
    CodeOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { Utils } from '../../../utils/utils';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { AI_HUB_SUBSYSTEMS } from '../ai-hub-routes';

const { Text, Title } = Typography;

interface MetaRow {
    setting_key: string;
    is_confidential: number | boolean;
    allowed_subsystems: string | string[];
    display_name: string | null;
    description: string | null;
}

interface SharedPromptLibraryPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string>;
    configTableRows: {
        shared_prompt_library_meta?: MetaRow[];
    };
}

const SUBSYSTEM_OPTIONS = [
    { value: '*', label: 'All subsystems (*)' },
    ...AI_HUB_SUBSYSTEMS.map((s) => ({ value: s.id, label: s.displayName })),
];

// Entries whose structure is load-bearing outside the AI layer — shown as a
// warning above the editor.
const KEY_EDIT_WARNINGS: Record<string, string> = {
    ELEMENT_DESCRIPTIONS:
        'Do NOT rename the JSON keys. They are canonical element names ("Alignment Depth", "Distribution Intensity", "Standardised NPS", …) that the dashboard visualizations match against for tooltips — renaming a key to its display name (Touchpoints, Availability, Advocacy Intent) breaks those lookups. The description text inside the values is safe to edit freely; old element names in any text sent to the AI are rewritten to the display names automatically.',
};

interface JsonProbe {
    looksLikeJson: boolean;
    parseError: string | null;
    parsed: unknown;
}

function probeJson(value: string): JsonProbe {
    const trimmed = value.trim();
    const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
    if (!looksLikeJson) {
        return { looksLikeJson: false, parseError: null, parsed: null };
    }
    try {
        return { looksLikeJson: true, parseError: null, parsed: JSON.parse(trimmed) };
    } catch (err) {
        return { looksLikeJson: true, parseError: (err as Error).message, parsed: null };
    }
}

function parseAllowed(value: string | string[]): string[] {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/**
 * Configure tab body for the Shared Prompt Library subsystem.
 *
 * Cards per entry showing the value (editable) plus a confidentiality badge
 * and the allowed_subsystems list. SUPER_ADMIN can edit allowed_subsystems via
 * a modal; non-SUPER_ADMIN sees them read-only.
 *
 * The placeholder picker is intentionally NOT used here — these entries ARE
 * the source of placeholders, so referencing them recursively would be
 * confusing. Plain textarea edits.
 */
export const SharedPromptLibraryConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<SharedPromptLibraryPayload>();
    const { user } = useAuth();
    const isSuperAdmin = user ? Utils.userIsSuperAdmin(user) : false;

    const [allowedEditing, setAllowedEditing] = useState<{
        key: string;
        allowed: string[];
    } | null>(null);

    const metaByKey = useMemo(() => {
        const map = new Map<string, MetaRow>();
        for (const row of editor.payload?.configTableRows?.shared_prompt_library_meta ?? []) {
            map.set(row.setting_key, row);
        }
        return map;
    }, [editor.payload]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    const setValue = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setAllowed = (key: string, allowed: string[]) => {
        editor.setPayload((prev) => {
            if (!prev) return prev as never;
            const rows = prev.configTableRows.shared_prompt_library_meta ?? [];
            const nextRows = rows.map((r) =>
                r.setting_key === key ? { ...r, allowed_subsystems: allowed } : r,
            );
            return {
                ...prev,
                configTableRows: { ...prev.configTableRows, shared_prompt_library_meta: nextRows },
            };
        });
    };

    const entries = Object.keys(editor.payload.systemSettingsKeys).sort();
    if (entries.length === 0) {
        return <Empty description="No shared prompt library entries — has the v1 baseline been seeded?" />;
    }

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Shared {`{PLACEHOLDER}`} fragments referenced by other subsystem prompts. Edits go into a draft
                    and only apply to live prompts after Publish.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            {entries.map((key) => {
                const meta = metaByKey.get(key);
                const isConfidential = meta?.is_confidential === true || meta?.is_confidential === 1;
                const allowed = parseAllowed(meta?.allowed_subsystems ?? '[]');
                const value = editor.payload!.systemSettingsKeys[key] ?? '';
                const probe = probeJson(value);

                return (
                    <Card
                        key={key}
                        size="small"
                        style={{ marginBottom: 12 }}
                        title={
                            <Space>
                                <code>{key}</code>
                                {meta?.display_name && (
                                    <Text type="secondary">{meta.display_name}</Text>
                                )}
                                {isConfidential && (
                                    <Tooltip
                                        title={`Only ${allowed.join(', ')} may reference this placeholder.`}
                                    >
                                        <Tag color="gold" icon={<LockOutlined />}>
                                            confidential
                                        </Tag>
                                    </Tooltip>
                                )}
                            </Space>
                        }
                        extra={
                            <Space size="small" wrap>
                                {allowed.length === 1 && allowed[0] === '*' ? (
                                    <Tag>any subsystem</Tag>
                                ) : (
                                    allowed.map((s) => (
                                        <Tag key={s} color="blue">
                                            {s}
                                        </Tag>
                                    ))
                                )}
                                {isSuperAdmin && (
                                    <Tooltip title="SUPER_ADMIN — manage allowed subsystems">
                                        <a
                                            onClick={() =>
                                                setAllowedEditing({ key, allowed: [...allowed] })
                                            }
                                        >
                                            <SettingOutlined /> manage
                                        </a>
                                    </Tooltip>
                                )}
                            </Space>
                        }
                    >
                        {meta?.description && (
                            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                                {meta.description}
                            </Text>
                        )}
                        {KEY_EDIT_WARNINGS[key] && (
                            <Alert
                                type="warning"
                                showIcon
                                message="JSON keys are load-bearing"
                                description={KEY_EDIT_WARNINGS[key]}
                                style={{ marginBottom: 8 }}
                            />
                        )}
                        {probe.looksLikeJson && (
                            <Space style={{ marginBottom: 8 }} wrap>
                                <Tag color={probe.parseError ? 'red' : 'green'}>
                                    {probe.parseError ? 'Invalid JSON' : 'JSON'}
                                </Tag>
                                <Button
                                    size="small"
                                    icon={<CodeOutlined />}
                                    disabled={!!probe.parseError}
                                    onClick={() =>
                                        setValue(
                                            key,
                                            JSON.stringify(probe.parsed, null, 2),
                                        )
                                    }
                                >
                                    Format JSON
                                </Button>
                            </Space>
                        )}
                        <Input.TextArea
                            rows={probe.looksLikeJson ? 14 : 6}
                            value={value}
                            onChange={(e) => setValue(key, e.target.value)}
                            placeholder={`(empty) — sets the value of ${key}`}
                            style={{ fontFamily: 'monospace' }}
                        />
                        {probe.parseError && (
                            <Alert
                                style={{ marginTop: 8 }}
                                type="warning"
                                showIcon
                                message="JSON parse error"
                                description={probe.parseError}
                            />
                        )}
                    </Card>
                );
            })}

            <Modal
                open={!!allowedEditing}
                title={
                    allowedEditing ? `Allowed subsystems for {${allowedEditing.key}}` : ''
                }
                onCancel={() => setAllowedEditing(null)}
                onOk={() => {
                    if (!allowedEditing) return;
                    if (allowedEditing.allowed.length === 0) {
                        message.warning('Pick at least one subsystem (or use *).');
                        return;
                    }
                    setAllowed(allowedEditing.key, allowedEditing.allowed);
                    setAllowedEditing(null);
                }}
                okText="Apply (still need to Save draft + Publish)"
            >
                <Alert
                    type="warning"
                    showIcon
                    message="Confidentiality scope — be careful"
                    description={
                        'Adding a subsystem here lets it resolve this placeholder at runtime AND reference it from its own prompts. Removing a subsystem will cause Layer 4 to start denying it. The change applies after Save draft + Publish.'
                    }
                    style={{ marginBottom: 12 }}
                />
                <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    value={allowedEditing?.allowed ?? []}
                    onChange={(allowed) =>
                        setAllowedEditing((prev) => (prev ? { ...prev, allowed } : prev))
                    }
                    options={SUBSYSTEM_OPTIONS}
                    placeholder="Pick allowed subsystems"
                />
            </Modal>
        </div>
    );
};
